import { api } from "jiffy-api";
import { getUploadTimeout } from "common/upload-constants";
import { downscaleClaimImage } from "common/claim-image-downscale";
import { notifyError } from "common/utils/app-monitoring/notify";

import "./claim-image-uploader.scss";

const $getUploadButton = () => document.querySelector(".js-claim-images-button");
const $getUploaderInfoContainer = () => document.querySelector(".js-claim-uploader-info");
const $getUploadInfoLabel = () => document.querySelector(".js-claim-upload-info-label");
const $getImageFilesSelector = () => document.querySelector(".js-claim-image-files");
const $getImageIdsField = () => document.querySelector(".js-claim-image-ids");
const $getDiscardButton = () => document.querySelector(".js-claim-discard-button");

// PO-1366: keep keys aligned with `record_rejection`'s reason symbol in
// app/controllers/spree/requests/claims/requests_controller.rb.
// Mirror in frontend/components/supplier_admin/made/create-claim/image-upload-handler.js.
const REJECTION_MESSAGES = {
  unsupported_format: "unsupported file format",
  too_large: "file is too large (50 MB max) — upload one photo at a time or reduce its resolution",
  mime_mismatch: "file content does not match its declared format",
  live_photo_video:
    "this looks like an iPhone Live Photo or video. Open the photo, tap Share → " +
    '"Duplicate as Still Photo" (or turn Live off), then upload the still image',
  sanitize_failed: "SVG could not be sanitized for safe display",
  upload_failed: "upload failed — please try again"
};

const formatRejection = ({ filename, reason }) => {
  const label = REJECTION_MESSAGES[reason] || reason || "rejected";
  return `${filename}: ${label}`;
};

const claimUploadTimeout = files =>
  getUploadTimeout(Array.from(files).reduce((sum, f) => sum + (f.size || 0), 0));

// MN-8909: gated by the claim_client_image_downscale flag, rendered onto the
// file input. Off => files upload raw exactly as before.
const isDownscaleEnabled = () => $getImageFilesSelector().dataset.clientDownscale === "true";

// Best-effort: downscaleClaimImage never throws and returns the original file
// on any failure, so a failed downscale still uploads the untouched file.
const prepareFile = async file => {
  const { file: prepared, status } = await downscaleClaimImage(file);
  if (status === "failed") {
    notifyError("Claim image client-side downscale failed", {
      // Bound the user-controlled filename before it reaches monitoring.
      params: { filename: file.name.slice(0, 256), type: file.type, size: file.size }
    });
  }
  return prepared;
};

// MN-5719: forward order ownership proof so the controller can authorize the
// upload. Hidden fields live on the parent claim form.
const buildFormData = async files => {
  const formData = new FormData();
  const orderNumberField = document.querySelector("input[name='order_number']");
  const lastNameField = document.querySelector("input[name='last_name']");
  if (orderNumberField) formData.append("order_number", orderNumberField.value);
  if (lastNameField) formData.append("last_name", lastNameField.value);

  const fileList = Array.from(files);
  fileList.forEach(file => {
    if (!file.type.startsWith("image/")) {
      throw new Error("You can't upload files of this type.");
    }
  });

  // Sequential by design: downscaling rasterizes onto a canvas, so processing
  // several large images concurrently can spike memory and OOM mobile browsers.
  const downscale = isDownscaleEnabled();
  for (const file of fileList) {
    // eslint-disable-next-line no-await-in-loop
    formData.append("images[]", downscale ? await prepareFile(file) : file);
  }
  return formData;
};

const applyUploadResponse = (response, uploadInfoLabel) => {
  const imageIds = Array.isArray(response.image_ids) ? response.image_ids : [];
  const rejected = Array.isArray(response.rejected) ? response.rejected : [];

  if (rejected.length > 0) {
    // eslint-disable-next-line no-alert
    alert(`Some files were rejected:\n\n${rejected.map(formatRejection).join("\n")}`);
  }

  if (imageIds.length === 0) {
    // Single-batch UI: the upload button hides on first attempt (success or
    // failure) and discard clears the field before any retry, so no prior IDs
    // can be in the field here. Skip the explicit clear as defensive behavior.
    uploadInfoLabel.innerText =
      rejected.length > 0
        ? "No files were accepted. Please try a different file."
        : "Upload failed — please try again.";
  } else {
    uploadInfoLabel.innerText = `${imageIds.length} images uploaded`;
    $getImageIdsField().value = imageIds.join(",");
  }
};

const uploadFiles = async (files, uploadInfoLabel) =>
  api.ordersClaims.uploadImages(await buildFormData(files), {
    raw: true,
    useAxiosForRequest: true,
    headers: { "Content-Type": undefined },
    timeout: claimUploadTimeout(files),
    onUploadProgress: ({ progress }) => {
      const currProgress = (progress * 100).toFixed();
      uploadInfoLabel.innerText = `Uploading ${currProgress}%`;
    }
  });

const handleImagesSelected = async event => {
  event.preventDefault();

  const { files } = event.target;

  if (!files.length) return;

  const uploadInfoLabel = $getUploadInfoLabel();

  $getUploadButton().classList.add("hidden");
  $getUploaderInfoContainer().classList.remove("hidden");
  uploadInfoLabel.innerText = "Uploading...";

  try {
    const response = await uploadFiles(files, uploadInfoLabel);
    applyUploadResponse(response, uploadInfoLabel);
  } catch (error) {
    uploadInfoLabel.innerText = error.message;
    console.error("Claim image upload: ", error);
  } finally {
    $getDiscardButton().classList.remove("hidden");
  }
};

const handleUploadButtonClick = event => {
  event.preventDefault();

  $getImageFilesSelector().click();
};

const handleDiscardButtonClick = event => {
  event.preventDefault();

  $getUploadButton().classList.remove("hidden");
  $getUploaderInfoContainer().classList.add("hidden");
  $getDiscardButton().classList.add("hidden");

  $getImageIdsField().value = "";
  $getImageFilesSelector().value = "";
};

const initClaimImageUploader = () => {
  $getImageFilesSelector().addEventListener("change", handleImagesSelected);
  $getUploadButton().addEventListener("click", handleUploadButtonClick);
  $getDiscardButton().addEventListener("click", handleDiscardButtonClick);
};

export default initClaimImageUploader;
